import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { useMutation } from '@apollo/react-hooks';
import { Fieldset } from 'primereact/fieldset';
import { injectIntl } from 'react-intl';
import UPDATE_RESPONDENT_DETAILS from '../mutations';
import buildValidationSchema from '../validation';
import Debug from '../../../../components/Debug';
import { LegacyInputTextComponent, DropdownWithValidation } from '../../../../components/prime-formik';
import { withGrowl } from '../../../../components/GrowlNotifications';
import hashCode from '../../../../utils/StringUtil';


const ariMessages = {
  noAnswerLabel: {
    id: 'ari.respondent.edit.dialog.noAnswer',
    defaultMessage: 'No answer',
  },
  updateLabel: {
    id: 'ari.respondent.edit.dialog.updateLabel',
    defaultMessage: 'Update',
  },
  globalFieldsetLegend: {
    id: 'ari.respondent.display.global.header',
    defaultMessage: 'Global',
  },
  localFieldsetLegend: {
    id: 'ari.respondent.display.local.header',
    defaultMessage: 'Local',
  },
  nothingToUpdate: {
    id: 'ari.respondent.edit.no.update',
    defaultMessage: 'Nothing to update',
  },
  overwriteSuccessSummary: {
    id: 'ari.respondent.edit.overwrite.success.summary',
    defaultMessage: 'Success updating',
  },
  overwriteSuccessDetail: {
    id: 'ari.respondent.edit.overwrite.success.summary',
    defaultMessage: 'Answers were successfully overwritten.',
  },
  overwriteFailSummary: {
    id: 'ari.respondent.edit.overwrite.fail.summary',
    defaultMessage: 'Error updating.',
  },
  overwriteFailDetail: {
    id: 'ari.respondent.edit.overwrite.fail.detail',
    defaultMessage: 'Something went wrong overwriting answers.',
  },
};

const OverwriteAriAnswers = ({
  currentAccount, respondent, intl: { formatMessage }, setIsVisible, accountGlobalAri, accountLocalAri, growl,
}) => {
  const [updateRespondentDetails] = useMutation(UPDATE_RESPONDENT_DETAILS,
    { refetchQueries: ['getAccount', 'getRespondent'] });

  const [initialValues, setInitialValues] = useState(null);
  const [validationSchema, setValidationSchema] = useState(null);

  useEffect(() => {
    if (accountLocalAri && accountGlobalAri) {
      const tempSchema = buildValidationSchema(formatMessage,
        {
          globalAriFields: accountGlobalAri.map(gf => ({
            id: gf.id,
            inputType: gf.dataType,
            question: gf.question,
            mandatory: gf.mandatory,
          })),
          localAriFields: accountLocalAri.map(lf => ({
            id: lf.id,
            inputType: lf.dataType,
            question: lf.question,
            mandatory: lf.mandatory,
          })),
        });
      const tempInitialValues = {};

      accountGlobalAri.forEach((gf) => {
        tempInitialValues[`global_${gf.id}_${hashCode(gf.question)}`] = '';
      });

      accountLocalAri.forEach((lf) => {
        tempInitialValues[`local_${lf.id}_${hashCode(lf.question)}`] = '';
      });
      setValidationSchema(tempSchema);
      setInitialValues(tempInitialValues);
    }
  }, [accountGlobalAri, accountLocalAri, formatMessage]);

  return initialValues && validationSchema && (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        const input = {
          respondentId: respondent.id,
          firstName: respondent.firstName,
          lastName: respondent.lastName,
          email: respondent.email,
          notes: respondent.notes,
          gender: respondent.gender,
          locale: respondent.locale,
          type: respondent.type,
          accountId: currentAccount,
          ariFields: [
            ...accountGlobalAri.map(gf => ({
              ariFieldId: gf.id,
              question: gf.question,
              mandatory: gf.mandatory,
              answer: values[`global_${gf.id}_${hashCode(gf.question)}`],
              ariFieldType: 'GLOBAL',
              ariInputType: gf.dataType,
              dropdownAnswers: gf.answers,
            })),
            ...accountLocalAri.map(lf => ({
              ariFieldId: lf.id,
              question: lf.question,
              mandatory: lf.mandatory,
              answer: values[`local_${lf.id}_${hashCode(lf.question)}`],
              ariFieldType: 'LOCAL',
              ariInputType: lf.dataType,
              dropdownAnswers: lf.answers,
            })),
          ],
        };

        updateRespondentDetails({ variables: { input } })
          .then(() => {
            growl.show({
              severity: 'success',
              sticky: false,
              summary: formatMessage(ariMessages.overwriteSuccessSummary),
              detail: formatMessage(ariMessages.overwriteSuccessDetail),
            });
            setSubmitting(false);
            setIsVisible(false);
          })
          .catch(() => {
            growl.show({
              severity: 'error',
              sticky: true,
              summary: formatMessage(ariMessages.overwriteFailSummary),
              detail: formatMessage(ariMessages.overwriteFailDetail),
            });
          });
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="p-grid p-justify-center" style={{ padding: '5px' }}>
            <Fieldset
              className="p-col"
              legend={formatMessage(ariMessages.globalFieldsetLegend)}
              style={{ margin: '5px' }}
            >
              {accountGlobalAri.length === 0 ? (
                <p style={{ textAlign: 'center' }}>
                  {formatMessage(ariMessages.nothingToUpdate)}
                </p>
              )
                : (accountGlobalAri.map((gf, index) => {
                  const dropdownOptions = [
                    {
                      key: 'No answer',
                      label: formatMessage(ariMessages.noAnswerLabel),
                      value: '',
                    },
                    ...gf.answers.map(answer => ({
                      key: answer,
                      label: answer,
                      value: answer,
                    })),
                  ];
                  return (
                    <div
                      className="p-col-12"
                      style={{ textAlign: 'center' }}
                      key={gf.id}
                    >
                      <div className="form-group" key={gf.question} style={{ marginBottom: '5px' }}>
                        <label
                          className="field-label"
                          htmlFor={`global${index + 1}`}
                        >
                          {`${gf.question}${gf.mandatory ? '*' : ''}`}
                        </label>
                      </div>
                      {gf.dataType === 'TEXT'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {gf.dataType === 'NUMBER'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {gf.dataType === 'DROPDOWN'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={DropdownWithValidation}
                            options={dropdownOptions}
                          />
                        )}
                    </div>
                  );
                }))}
            </Fieldset>
            <Fieldset
              className="p-col"
              legend={formatMessage(ariMessages.localFieldsetLegend)}
              style={{ margin: '5px' }}
            >
              {accountLocalAri.length === 0
                ? (
                  <p style={{ textAlign: 'center' }}>
                    {formatMessage(ariMessages.nothingToUpdate)}
                  </p>
                )
                : (accountLocalAri.map((lf, index) => {
                  const dropdownOptions = [
                    {
                      key: 'No answer',
                      label: formatMessage(ariMessages.noAnswerLabel),
                      value: '',
                    },
                    ...lf.answers.map(answer => ({
                      key: answer,
                      label: answer,
                      value: answer,
                    })),
                  ];
                  return (
                    <div
                      className="p-col-12"
                      style={{ textAlign: 'center' }}
                      key={lf.id}
                    >
                      <div className="form-group" key={lf.question} style={{ marginBottom: '5px' }}>
                        <label
                          className="field-label"
                          htmlFor={`local${index + 1}`}
                        >
                          {`${lf.question}${lf.mandatory ? '*' : ''}`}
                        </label>
                      </div>
                      {lf.dataType === 'TEXT'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {lf.dataType === 'NUMBER'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {lf.dataType === 'DROPDOWN'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={DropdownWithValidation}
                            options={dropdownOptions}
                          />
                        )}
                    </div>
                  );
                }))}
            </Fieldset>
            <div className="p-col-12" style={{ textAlign: 'center' }}>
              <Button label={formatMessage(ariMessages.updateLabel)} type="submit" disabled={isSubmitting} />
            </div>
          </div>
          <Debug />
        </Form>
      )}
    </Formik>
  );
};

OverwriteAriAnswers.propTypes = {
  accountGlobalAri: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  accountLocalAri: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  currentAccount: PropTypes.string.isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
  setIsVisible: PropTypes.func.isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(OverwriteAriAnswers));
